import axios from "axios";
import api from "./axiosConfig";

export const DOCUMENT_CATEGORIES = [
  "CreditReport",
  "ID",
  "ProofOfAddress",
  "DisputeLetter",
  "BureauResponse",
  "ServiceAgreement",
  "Contract",
  "Disclosure",
  "RightsDisclosure",
  "Other",
] as const;

export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  CreditReport: "Credit Report",
  ID: "ID Verification",
  ProofOfAddress: "Proof of Address",
  DisputeLetter: "Dispute Letter",
  BureauResponse: "Bureau Response",
  ServiceAgreement: "Service Agreement",
  Contract: "Contract",
  Disclosure: "Rights disclosure",
  RightsDisclosure: "Rights disclosure (file)",
  Other: "Other",
};

export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

export const ALLOWED_DOCUMENT_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".doc", ".docx"] as const;

export interface ClientDocument {
  id: string;
  clientId: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  category: string;
  description?: string | null;
  uploadedBy?: string | null;
  createdAt: string;
}

export function validateDocumentFile(file: File): string | null {
  const name = file.name || "";
  const lower = name.toLowerCase();
  const dot = lower.lastIndexOf(".");
  const ext = dot >= 0 ? lower.slice(dot) : "";
  if (!ext || !(ALLOWED_DOCUMENT_EXTENSIONS as readonly string[]).includes(ext)) {
    return `File type is not allowed. Use: ${ALLOWED_DOCUMENT_EXTENSIONS.join(", ")}`;
  }
  if (file.size > MAX_DOCUMENT_BYTES) {
    return "File size exceeds 10MB limit.";
  }
  return null;
}

function readUploadError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const d = err.response?.data;
    if (typeof d === "string" && d.trim()) {
      return d.trim();
    }
    if (d && typeof d === "object") {
      const o = d as Record<string, unknown>;
      if (typeof o.detail === "string" && o.detail.trim()) {
        return o.detail.trim();
      }
      if (typeof o.title === "string" && typeof o.detail === "string") {
        return o.detail.trim();
      }
      if (typeof o.message === "string" && o.message.trim()) {
        return o.message.trim();
      }
    }
  }
  return fallback;
}

export const documentsApi = {
  list: async (clientId: string): Promise<ClientDocument[]> => {
    const { data } = await api.get<ClientDocument[]>(`/clients/${clientId}/documents`);
    return data;
  },

  upload: async (
    clientId: string,
    file: File,
    category: string,
    description?: string | null
  ): Promise<ClientDocument> => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    if (description?.trim()) {
      fd.append("description", description.trim());
    }
    try {
      const { data } = await api.post<ClientDocument>(`/clients/${clientId}/documents`, fd);
      return data;
    } catch (e) {
      throw new Error(readUploadError(e, "Upload failed. Try a smaller file or check your connection."));
    }
  },

  remove: async (documentId: string): Promise<void> => {
    await api.delete(`/documents/${documentId}`);
  },

  /** Downloads file (axios follows redirects, e.g. to Azure SAS). */
  download: async (documentId: string, fileName: string): Promise<void> => {
    const res = await api.get(`/documents/${documentId}/download`, { responseType: "blob" });
    const blob = res.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};
